import { useCallback, useEffect, useState } from "react";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Select from "@mui/material/Select";
import Tab from "@mui/material/Tab";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { makeStyles } from "@mui/styles";
import { Link } from "react-router-dom";
import ConfirmModal from "./ConfirmModal";
import DismissibleMessage from "./DismissibleMessage";
import { ClientsDropdown, DatetimeDropdown, DropdownWithAutocomplete, ProjectsDropdown } from "./DropdownV2";
import LoadingOverlay from "../../core/LoadingOverlay";
import MapleMap from "./MapleMap";
import { calibrationService, DEFAULT_GROUP_KEY } from "../../../services/CalibrationService";

const fmt3 = (v) => parseFloat(v).toFixed(3);

const SIMULATION_AREAS_DATASET_TYPE = "palm-simulation-areas";
const SIMULATION_SPANS_DATASET_TYPE = "palm-simulation-spans";
const NONCONFORMITY_DATASET_TYPE = "maple-bam-nonconformity";
const SPAN_COEFFICIENTS_DATASET_TYPE = "span-palm-coefficients";


const RATING_METHODOLOGY_OPTIONS = [
    { label: "Legacy", value: "legacy" },
    { label: "Span Weighted Sensorless", value: "span_weighted_sensorless" },
    { label: "Span Weighted Sensor Verified", value: "span_weighted_sensor_verified" },
    { label: "Span Weighted Sensor Calibrated", value: "span_weighted_sensor_calibrated" },
];

const TABS = {
    TRIGGER_MAPLE: "Trigger Maple",
    BAM_UPLOAD: "BAM Upload",
    SECTION_RATING_METHODOLOGY: "Section Rating Methodology",
};
const TAB_LIST = Object.values(TABS);

const STATUS_CHIP_COLOR = {
    "Upload ready": "success",
    "Validation warnings": "warning",
    Uploaded: "primary",
};

const getCenterOfAllAreas = (areas) => {
    let totalLat = 0;
    let totalLng = 0;
    areas.forEach((area) => {
        const { max_latitude, min_latitude, max_longitude, min_longitude } = area.bbox;
        totalLat += (+max_latitude + +min_latitude) / 2;
        totalLng += (+max_longitude + +min_longitude) / 2;
    });
    return { lat: totalLat / areas.length, lng: totalLng / areas.length };
};

const useStyles = makeStyles((theme) => ({
    formContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        width: "95%",
        margin: "0 auto",
        padding: theme.spacing(3),
    },
    selectContainer: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        width: "100%",
        marginBottom: theme.spacing(2),
    },
    tabDatasetSelect: {
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        gap: theme.spacing(2),
        width: "100%",
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
        flexWrap: "wrap",
    },
    buttonContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: theme.spacing(2),
        margin: theme.spacing(2, 0),
    },
    sectionCard: {
        width: "100%",
        padding: theme.spacing(3),
        marginTop: theme.spacing(2),
        border: "1px solid #e0e0e0",
    },
    statusRow: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
}));

export default function Maple() {
    const classes = useStyles();

    // Shared top-level state
    const [client, setClient] = useState(null);
    const [project, setProject] = useState(null);
    const [selectedTab, setSelectedTab] = useState(0);
    const [error, setError] = useState(null);
    const [statusMessage, setStatusMessage] = useState(null);

    // Tab 0 — Trigger Maple
    const [ratingMethodology, setRatingMethodology] = useState("span_weighted_sensor_calibrated");
    const [weatherSiteBamOnly, setWeatherSiteBamOnly] = useState(false);
    const [mapleStartDate, setMapleStartDate] = useState("");
    const [mapleEndDate, setMapleEndDate] = useState("");
    const [bamOutlierHigh, setBamOutlierHigh] = useState("");
    const [triggerStatus, setTriggerStatus] = useState(null);

    // Tab 2 — Section Rating Methodology
    const [sectionRatingMethodology, setSectionRatingMethodology] = useState("");
    const [sectionMethodologySummary, setSectionMethodologySummary] = useState(null);
    const [isSectionMethodologySummaryLoading, setIsSectionMethodologySummaryLoading] = useState(false);
    const [refreshSummary, setRefreshSummary] = useState(0);
    const [toggleMethodology, setToggleMethodology] = useState(false);

    // Tab 1 — BAM Upload
    const [simAreaVersion, setSimAreaVersion] = useState(null);
    const [simAreaDataset, setSimAreaDataset] = useState(null);
    const [simSpanDataset, setSimSpanDataset] = useState(null);
    const [loadingSimDatasets, setLoadingSimDatasets] = useState(false);
    const [palmCoeffVersion, setPalmCoeffVersion] = useState(null);
    const [palmCoeffDataset, setPalmCoeffDataset] = useState(null);
    const [bamStatusVersion, setBamStatusVersion] = useState(null);
    const [bamStatus, setBamStatus] = useState(null);
    const [spanBamsDataset, setSpanBamsDataset] = useState(null);
    const [weatherSiteBamsDataset, setWeatherSiteBamsDataset] = useState(null);
    const [compareBamsVersion, setCompareBamsVersion] = useState(null);
    const [compareBamsDataset, setCompareBamsDataset] = useState(null);
    const [checkingBamStatus, setCheckingBamStatus] = useState(false);
    const [uploadAcknowledged, setUploadAcknowledged] = useState(false);
    const [uploadingBam, setUploadingBam] = useState(false);
    const [uploadMode, setUploadMode] = useState("current");
    const [customCalibrationDate, setCustomCalibrationDate] = useState("");
    const [mapCenter, setMapCenter] = useState({ lat: 0, lng: 0 });
    const [mapZoom, setMapZoom] = useState(9);
    const [acknowledgedOutliers, setAcknowledgedOutliers] = useState(new Set());
    const [acknowledgedSpanFailures, setAcknowledgedSpanFailures] = useState(new Set());
    const [satelliteView, setSatelliteView] = useState(true);
    const [layerVisibility, setLayerVisibility] = useState({
        coefficients: true,
        spanBams: true,
        weatherSiteBams: true,
        bamDiff: true,
        bamOutliers: true,
        deviceFailures: true,
        spanFailures: true,
    });

    // Reset project when client changes
    useEffect(() => {
        setProject(null);
    }, [client]);

    // Reset tab-level state when client/project changes
    useEffect(() => {
        setSimAreaVersion(null);
        setSimAreaDataset(null);
        setSimSpanDataset(null);
        setPalmCoeffVersion(null);
        setPalmCoeffDataset(null);
        setBamStatusVersion(null);
        setBamStatus(null);
        setSpanBamsDataset(null);
        setCompareBamsVersion(null);
        setCompareBamsDataset(null);
        setAcknowledgedOutliers(new Set());
        setAcknowledgedSpanFailures(new Set());
        setTriggerStatus(null);
        setSectionRatingMethodology("");
        setSectionMethodologySummary(null);
        setError(null);
        setStatusMessage(null);
    }, [client, project]);

    // ── Fetch section methodology summary when client/project/refreshSummary changes ─
    useEffect(() => {
        if (!client || !project) return;

        let cancelled = false;
        const fetchSectionSummary = async () => {
            setIsSectionMethodologySummaryLoading(true);
            try {
                const response = await calibrationService.getSectionMethodologySummary(client, project);
                if (!cancelled) setSectionMethodologySummary(response.data);
            } catch (err) {
                if (!cancelled) {
                    const errorMessage = err?.response?.data?.error || err?.message || err;
                    setError(`Failed to fetch section methodology summary: ${errorMessage}`);
                }
            } finally {
                if (!cancelled) setIsSectionMethodologySummaryLoading(false);
            }
        };

        fetchSectionSummary();
        return () => {
            cancelled = true;
        };
    }, [project, refreshSummary]);

    const changeRatingMethodology = useCallback(async () => {
        setToggleMethodology(true);
        try {
            await calibrationService.bulkToggleSectionMethodology(client, project, sectionRatingMethodology);
            setStatusMessage(`Successfully changed rating methodology to ${sectionRatingMethodology}`);
            setRefreshSummary((prev) => prev + 1);
        } catch (err) {
            const errorMessage = err?.response?.data?.error || err?.message || err;
            setError(`Failed to change rating methodology: ${errorMessage}`);
        } finally {
            setToggleMethodology(false);
        }
    }, [client, project, sectionRatingMethodology]);

    const handleTabChange = (_evt, newValue) => {
        setSelectedTab(newValue);
        setError(null);
        setStatusMessage(null);
    };

    // ── Fetch sim areas + spans when simAreaVersion changes ──────────────────
    useEffect(() => {
        if (!simAreaVersion || !client || !project) {
            setSimAreaDataset(null);
            setSimSpanDataset(null);
            return;
        }

        let cancelled = false;
        const fetchSim = async () => {
            setLoadingSimDatasets(true);
            try {
                const areasResult = await calibrationService.getExactDataset(
                    client,
                    project,
                    DEFAULT_GROUP_KEY,
                    SIMULATION_AREAS_DATASET_TYPE,
                    simAreaVersion
                );
                if (!cancelled) setSimAreaDataset(areasResult.data);
            } catch {
                if (!cancelled) setError("Failed to load simulation areas dataset.");
                setLoadingSimDatasets(false);
                return;
            }

            try {
                const spansResult = await calibrationService.getExactDataset(
                    client,
                    project,
                    DEFAULT_GROUP_KEY,
                    SIMULATION_SPANS_DATASET_TYPE,
                    simAreaVersion
                );
                if (!cancelled) setSimSpanDataset(spansResult.data);
            } catch (spansErr) {
                if (!cancelled) {
                    if (spansErr?.response?.status === 404) {
                        setError(
                            "Simulation spans dataset not found. Contact OBS SWEs to regenerate simulation areas for this project."
                        );
                    } else {
                        setError("Failed to load simulation spans dataset.");
                    }
                    setSimSpanDataset(null);
                }
            } finally {
                if (!cancelled) setLoadingSimDatasets(false);
            }
        };

        fetchSim();
        return () => {
            cancelled = true;
        };
    }, [simAreaVersion, client, project]);

    // ── Fetch span-palm-coefficients when version changes ────────────────────
    useEffect(() => {
        if (!palmCoeffVersion || !client || !project) {
            setPalmCoeffDataset(null);
            return;
        }

        let cancelled = false;
        const fetchCoeffs = async () => {
            try {
                const result = await calibrationService.getExactDataset(
                    client,
                    project,
                    DEFAULT_GROUP_KEY,
                    SPAN_COEFFICIENTS_DATASET_TYPE,
                    palmCoeffVersion
                );
                if (!cancelled) setPalmCoeffDataset(result.data);
            } catch {
                if (!cancelled) setError("Failed to load span coefficients dataset.");
            }
        };

        fetchCoeffs();
        return () => {
            cancelled = true;
        };
    }, [palmCoeffVersion, client, project]);

    // Update map center when sim area dataset loads
    useEffect(() => {
        if (simAreaDataset?.simulation_areas?.length) {
            setMapCenter(getCenterOfAllAreas(simAreaDataset.simulation_areas));
            setMapZoom(9);
        }
    }, [simAreaDataset]);

    // If no sim areas, center on nonconformity points when they arrive
    useEffect(() => {
        if (simAreaDataset?.simulation_areas?.length) return;
        const nc = bamStatus?.nonconformity;
        const points = [
            ...(nc?.bam_outliers ?? []).map((o) => ({
                lat: parseFloat(o.latitude),
                lng: parseFloat(o.longitude),
            })),
            ...(nc?.device_health_failures ?? []).map((f) => ({
                lat: parseFloat(f.latitude),
                lng: parseFloat(f.longitude),
            })),
        ];
        if (!points.length) return;
        const avgLat = points.reduce((s, p) => s + p.lat, 0) / points.length;
        const avgLng = points.reduce((s, p) => s + p.lng, 0) / points.length;
        setMapCenter({ lat: avgLat, lng: avgLng });
        setMapZoom(10);
    }, [bamStatus, simAreaDataset]);

    const flyToCoord = (lat, lng) => {
        setMapCenter({ lat: parseFloat(lat), lng: parseFloat(lng) });
        setMapZoom(14);
    };

    const toggleOutlierAck = (id) => {
        setAcknowledgedOutliers((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleSpanFailureAck = (id) => {
        setAcknowledgedSpanFailures((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleAllSignificantSpanFailuresAck = () => {
        const significantIds = spanFailures.filter((s) => Math.abs(s.change_percentage) >= 0.15).map((s) => s.span_id);
        const allAcked = significantIds.every((id) => acknowledgedSpanFailures.has(id));
        setAcknowledgedSpanFailures((prev) => {
            const next = new Set(prev);
            if (allAcked) {
                significantIds.forEach((id) => next.delete(id));
            } else {
                significantIds.forEach((id) => next.add(id));
            }
            return next;
        });
    };

    // ── Check BAM status when version changes ────────────────────────────────
    useEffect(() => {
        if (!bamStatusVersion || !client || !project) {
            setBamStatus(null);
            return;
        }

        let cancelled = false;
        const check = async () => {
            setCheckingBamStatus(true);
            setUploadAcknowledged(false);
            setAcknowledgedOutliers(new Set());
            setAcknowledgedSpanFailures(new Set());
            try {
                const result = await calibrationService.checkBamStatus(client, project, bamStatusVersion);
                if (!cancelled) setBamStatus(result);
            } catch {
                if (!cancelled) setError("Failed to fetch BAM status.");
            } finally {
                if (!cancelled) setCheckingBamStatus(false);
            }
        };

        check();
        return () => {
            cancelled = true;
        };
    }, [bamStatusVersion, client, project]);

    // ── Auto-fetch span BAMs when bamStatus provides a version_dttm ─────────
    useEffect(() => {
        if (!bamStatus?.version_dttm || !client || !project) {
            setSpanBamsDataset(null);
            return;
        }

        let cancelled = false;
        const fetchBams = async () => {
            try {
                const result = await calibrationService.getExactDataset(
                    client,
                    project,
                    DEFAULT_GROUP_KEY,
                    "maple-span-bams",
                    bamStatus.version_dttm
                );
                if (!cancelled) setSpanBamsDataset(result.data);
            } catch {
                // Span BAMs may not exist yet — that's fine
                if (!cancelled) setSpanBamsDataset(null);
            }
        };

        fetchBams();
        return () => {
            cancelled = true;
        };
    }, [bamStatus?.version_dttm, client, project]);

    // ── Auto-fetch weather site BAMs when bamStatus provides a version_dttm ──
    useEffect(() => {
        if (!bamStatus?.version_dttm || !client || !project) {
            setWeatherSiteBamsDataset(null);
            return;
        }

        let cancelled = false;
        const fetchBams = async () => {
            try {
                const result = await calibrationService.getExactDataset(
                    client,
                    project,
                    DEFAULT_GROUP_KEY,
                    "maple-weather-site-bams",
                    bamStatus.version_dttm
                );
                if (!cancelled) setWeatherSiteBamsDataset(result.data);
            } catch {
                if (!cancelled) setWeatherSiteBamsDataset(null);
            }
        };

        fetchBams();
        return () => {
            cancelled = true;
        };
    }, [bamStatus?.version_dttm, client, project]);

    // ── Fetch comparison BAMs when a comparison version is selected ──────────
    useEffect(() => {
        if (!compareBamsVersion || !client || !project) {
            setCompareBamsDataset(null);
            return;
        }

        let cancelled = false;
        const fetchCompare = async () => {
            try {
                const result = await calibrationService.getExactDataset(
                    client,
                    project,
                    DEFAULT_GROUP_KEY,
                    "maple-span-bams",
                    compareBamsVersion
                );
                if (!cancelled) setCompareBamsDataset(result.data);
            } catch {
                if (!cancelled) setCompareBamsDataset(null);
            }
        };

        fetchCompare();
        return () => {
            cancelled = true;
        };
    }, [compareBamsVersion, client, project]);

    // ── Trigger Maple ────────────────────────────────────────────────────────
    const handleTriggerMaple = () => {
        if (!client || !project || !ratingMethodology) return;
        setError(null);
        setTriggerStatus(null);

        const extra = { rating_methodology: ratingMethodology };
        if (weatherSiteBamOnly) extra.weather_site_bam_only = true;
        if (mapleStartDate) extra.start_date = mapleStartDate;
        if (mapleEndDate) extra.end_date = mapleEndDate;
        if (bamOutlierHigh !== "" && bamOutlierHighValid) {
            extra.bam_outlier_high = Number(bamOutlierHigh);
        }

        // Fire and forget — the ECS task runs for 15–20 min and will time out the HTTP
        // connection. Ignore network timeouts; surface any other error.
        calibrationService.triggerMaple(client, project, extra).catch((err) => {
            const isTimeout = err === "Network Error" || err?.code === "ERR_NETWORK";
            if (!isTimeout) {
                setError(`Failed to trigger Maple run: ${err}`);
            }
        });

        setTriggerStatus("Maple run submitted. Check the Progress Viewer for status.");
    };

    // ── BAM Upload ───────────────────────────────────────────────────────────
    const handleBamUpload = async () => {
        if (!bamStatus?.version_dttm) return;
        setUploadingBam(true);
        setError(null);

        try {
            await calibrationService.bamUpload(
                client,
                project,
                bamStatus.version_dttm,
                uploadMode === "epoch",
                uploadMode === "custom" ? customCalibrationDate : null
            );
            setStatusMessage(
                uploadMode === "epoch"
                    ? "Epoch BAM upload submitted successfully."
                    : uploadMode === "custom"
                      ? "Custom-datetime BAM upload submitted successfully."
                      : "BAM results uploaded successfully."
            );
            // Re-fetch status — should now show "Uploaded"
            const refreshed = await calibrationService.checkBamStatus(client, project, bamStatusVersion);
            setBamStatus(refreshed);
        } catch {
            setError("Failed to upload BAM results.");
        } finally {
            setUploadingBam(false);
        }
    };

    // ── Derived state ────────────────────────────────────────────────────────
    const hasWarnings = bamStatus?.status === "Validation warnings";
    const isUploaded = bamStatus?.status === "Uploaded";
    const canUpload =
        bamStatus &&
        !isUploaded &&
        bamStatus.version_dttm &&
        (!hasWarnings || uploadAcknowledged) &&
        (uploadMode !== "custom" || !!customCalibrationDate);
    const bamOutlierHighValid =
        bamOutlierHigh === "" ||
        (!Number.isNaN(Number(bamOutlierHigh)) && Number(bamOutlierHigh) > 0 && Number(bamOutlierHigh) < 10);

    const nonconformity = bamStatus?.nonconformity;
    const bamOutliers = nonconformity?.bam_outliers || [];
    const deviceFailures = nonconformity?.device_health_failures || [];
    const spanFailures = nonconformity?.span_validation_failures || [];
    const hasNonconformity = bamOutliers.length > 0 || deviceFailures.length > 0 || spanFailures.length > 0;

    // Map shows as soon as we have sim area data OR nonconformity data
    const showMap = !!(simAreaDataset || nonconformity || spanBamsDataset);

    // Acknowledged items are hidden from the map but stay in the side panel (dimmed)
    const visibleNonconformity = nonconformity
        ? {
              ...nonconformity,
              bam_outliers: bamOutliers.filter((o) => !acknowledgedOutliers.has(o.weather_site_id)),
              span_validation_failures: spanFailures.filter((s) => !acknowledgedSpanFailures.has(s.span_id)),
          }
        : null;

    // Compute BAM diffs when both current and comparison datasets are loaded
    const spanBamDiffs = (() => {
        if (!spanBamsDataset?.results || !compareBamsDataset?.results) return null;
        const compareMap = new Map();
        for (const b of compareBamsDataset.results) {
            compareMap.set(b.span_id, b);
        }
        return spanBamsDataset.results
            .filter((b) => b.latitude && b.longitude && compareMap.has(b.span_id))
            .map((b) => {
                const prev = compareMap.get(b.span_id);
                const curBam = parseFloat(b.bam);
                const prevBam = parseFloat(prev.bam);
                const diff = curBam - prevBam;
                const pctChange = prevBam !== 0 ? (diff / prevBam) * 100 : 0;
                return {
                    span_id: b.span_id,
                    latitude: b.latitude,
                    longitude: b.longitude,
                    bam: curBam,
                    prevBam,
                    diff,
                    pctChange,
                };
            });
    })();

    const toggleLayer = (key) => {
        setLayerVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const progressViewerUrl = `/calibration/progressViewer?client=${encodeURIComponent(
        client || ""
    )}&project=${encodeURIComponent(project || "")}&processTypes=TriggerMaple`;

    return (
        <div id="form-container" className={classes.formContainer}>
            <h1 style={{ textAlign: "center" }}>Maple BAM</h1>

            <div className={classes.selectContainer}>
                <ClientsDropdown value={client} setValue={setClient} styleClass={{ flex: 1, mx: 2, px: 0 }} />
                <ProjectsDropdown
                    clientName={client}
                    value={project}
                    setValue={setProject}
                    styleClass={{ flex: 1, mx: 2, px: 0 }}
                />
            </div>

            <DismissibleMessage message={error} type="error" onDismiss={() => setError(null)} />
            <DismissibleMessage message={statusMessage} type="success" onDismiss={() => setStatusMessage(null)} />
            <LoadingOverlay isLoading={uploadingBam} message="Uploading BAM results..." />
            <LoadingOverlay isLoading={isSectionMethodologySummaryLoading} message="Retrieving Section Summary..." />
            <LoadingOverlay isLoading={toggleMethodology} message="Toggling rating methodology..." />

            <Tabs
                value={selectedTab}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                centered
                variant="scrollable"
                scrollButtons="auto"
            >
                {TAB_LIST.map((label, i) => (
                    <Tab key={i} label={label} />
                ))}
            </Tabs>

            {/* ── Tab 0: Trigger Maple ─────────────────────────────────────── */}
            {selectedTab === 0 && (
                <Paper variant="outlined" className={classes.sectionCard}>
                    <Typography variant="h6" gutterBottom>
                        Trigger a Maple BAM Run
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                        Starts an ECS Fargate task for the selected client/project. The run is asynchronous (~15–20
                        min). Check the Progress Viewer for status.
                    </Typography>

                    <div
                        style={{ display: "flex", flexDirection: "column", gap: "16px", maxWidth: 500, marginTop: 16 }}
                    >
                        <FormControl size="small" required style={{ minWidth: 280 }}>
                            <InputLabel id="rating-methodology-label">Rating Methodology</InputLabel>
                            <Select
                                labelId="rating-methodology-label"
                                value={ratingMethodology}
                                label="Rating Methodology"
                                onChange={(e) => setRatingMethodology(e.target.value)}
                            >
                                {RATING_METHODOLOGY_OPTIONS.map((m) => (
                                    <MenuItem key={m.value} value={m.value}>
                                        {m.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={weatherSiteBamOnly}
                                    onChange={(e) => setWeatherSiteBamOnly(e.target.checked)}
                                />
                            }
                            label={
                                <div>
                                    <Typography variant="body2">Weather site BAMs only</Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        Mode A: compute weather site BAMs only, skip span BAMs
                                    </Typography>
                                </div>
                            }
                        />

                        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                            <TextField
                                label="Start Date"
                                type="date"
                                size="small"
                                value={mapleStartDate}
                                onChange={(e) => setMapleStartDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                helperText={mapleStartDate ? "" : "Default: per-site install date"}
                                style={{ minWidth: 180 }}
                            />
                            <TextField
                                label="End Date"
                                type="date"
                                size="small"
                                value={mapleEndDate}
                                onChange={(e) => setMapleEndDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                helperText={mapleEndDate ? "" : "Default: today (UTC)"}
                                style={{ minWidth: 180 }}
                            />
                        </div>

                        <TextField
                            label="BAM HIGH outlier threshold (override)"
                            type="number"
                            size="small"
                            value={bamOutlierHigh}
                            onChange={(e) => setBamOutlierHigh(e.target.value)}
                            error={!bamOutlierHighValid}
                            inputProps={{ step: 0.1, min: 0, max: 10 }}
                            helperText={
                                !bamOutlierHighValid
                                    ? "Must be a number greater than 0 and less than 10."
                                    : "Optional. Default is 2.0 (SOP). Raise this for projects where legitimate weather-site BAMs run higher (e.g. NGET HARSPNBLY PT3 needs 2.5). Leave blank to use the default."
                            }
                            style={{ maxWidth: 320 }}
                        />
                    </div>

                    <div className={classes.buttonContainer} style={{ justifyContent: "flex-start", marginTop: 24 }}>
                        <ConfirmModal
                            enableButton={!!(client && project && ratingMethodology) && bamOutlierHighValid}
                            operation={handleTriggerMaple}
                            buttonText="Run Maple"
                            buttonColor="primary"
                            modalTitle="Confirm Maple Run"
                            modalMessage={[
                                `Trigger a Maple BAM run for ${client} / ${project}`,
                                `Rating methodology: ${ratingMethodology}`,
                                `Start date: ${mapleStartDate || "per-site install date (default)"}`,
                                `End date: ${mapleEndDate || "today (default)"}`,
                                `BAM HIGH outlier threshold: ${
                                    bamOutlierHigh !== "" ? `${Number(bamOutlierHigh)} (override)` : "2.0 (default)"
                                }`,
                            ].join("\n\n")}
                            warningMessage={
                                weatherSiteBamOnly
                                    ? "Weather site BAMs only mode — span BAMs will not be computed."
                                    : undefined
                            }
                        />
                    </div>

                    {triggerStatus && (
                        <div style={{ marginTop: 8 }}>
                            <DismissibleMessage
                                message={triggerStatus}
                                type="success"
                                onDismiss={() => setTriggerStatus(null)}
                            />
                            <div style={{ textAlign: "center", marginTop: 4 }}>
                                <Link to={progressViewerUrl} target="_blank" rel="noopener noreferrer">
                                    View in Progress Viewer
                                </Link>
                            </div>
                        </div>
                    )}
                </Paper>
            )}

            {/* ── Tab 1: BAM Upload ────────────────────────────────────────── */}
            {selectedTab === 1 && (
                <Paper variant="outlined" className={classes.sectionCard}>
                    <Typography variant="h6" gutterBottom>
                        BAM Status &amp; Upload
                    </Typography>

                    <div className={classes.tabDatasetSelect}>
                        <div style={{ flex: 1, minWidth: 220, display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{ flex: 1 }}>
                                <DatetimeDropdown
                                    clientName={client}
                                    projectName={project}
                                    datasetType={NONCONFORMITY_DATASET_TYPE}
                                    group={DEFAULT_GROUP_KEY}
                                    value={bamStatusVersion}
                                    setValue={setBamStatusVersion}
                                    name="Nonconformity Version"
                                />
                            </div>
                            {checkingBamStatus && <CircularProgress size={20} />}
                        </div>
                        <div style={{ flex: 1, minWidth: 220, display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{ flex: 1 }}>
                                <DatetimeDropdown
                                    clientName={client}
                                    projectName={project}
                                    datasetType={SIMULATION_AREAS_DATASET_TYPE}
                                    group={DEFAULT_GROUP_KEY}
                                    value={simAreaVersion}
                                    setValue={setSimAreaVersion}
                                    name="Simulation Areas Version (optional)"
                                />
                            </div>
                            {loadingSimDatasets && <CircularProgress size={20} />}
                        </div>
                        <div style={{ flex: 1, minWidth: 220 }}>
                            <DatetimeDropdown
                                clientName={client}
                                projectName={project}
                                datasetType={SPAN_COEFFICIENTS_DATASET_TYPE}
                                group={DEFAULT_GROUP_KEY}
                                value={palmCoeffVersion}
                                setValue={setPalmCoeffVersion}
                                name="Coefficients Version (optional)"
                            />
                        </div>
                        <div style={{ flex: 1, minWidth: 220 }}>
                            <DatetimeDropdown
                                clientName={client}
                                projectName={project}
                                datasetType="maple-span-bams"
                                group={DEFAULT_GROUP_KEY}
                                value={compareBamsVersion}
                                setValue={setCompareBamsVersion}
                                name="Compare BAMs Against (optional)"
                            />
                        </div>
                    </div>

                    {/* Status chip */}
                    {bamStatus && (
                        <div className={classes.statusRow}>
                            <Typography variant="body2" style={{ fontWeight: 600 }}>
                                Status:
                            </Typography>
                            <Chip
                                label={bamStatus.status}
                                color={STATUS_CHIP_COLOR[bamStatus.status] || "default"}
                                size="small"
                            />
                            {bamStatus.version_dttm && (
                                <Typography variant="caption" color="textSecondary">
                                    Version: {bamStatus.version_dttm}
                                </Typography>
                            )}
                        </div>
                    )}
                    {bamStatusVersion && !checkingBamStatus && bamStatus === null && (
                        <div className={classes.statusRow}>
                            <Chip label="No run found" color="default" size="small" />
                        </div>
                    )}

                    {/* Acknowledgement checkbox for warnings */}
                    {hasWarnings && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={uploadAcknowledged}
                                    onChange={(e) => setUploadAcknowledged(e.target.checked)}
                                    color="warning"
                                />
                            }
                            label={
                                <Typography variant="body2" color="textSecondary">
                                    I have reviewed the nonconformity report and acknowledge the warnings above.
                                </Typography>
                            }
                            style={{ marginTop: 8 }}
                        />
                    )}

                    {/* Advanced options (epoch upload) */}
                    <Accordion
                        disableGutters
                        elevation={0}
                        sx={{ backgroundColor: "transparent", "&::before": { display: "none" }, mt: 1 }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, minHeight: 32 }}>
                            <Typography variant="caption" color="textSecondary">
                                Advanced Options
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 0, pt: 0 }}>
                            <Typography variant="caption" style={{ fontWeight: 600 }}>
                                Set Calibration Datetime
                            </Typography>
                            <RadioGroup
                                value={uploadMode}
                                onChange={(e) => setUploadMode(e.target.value)}
                            >
                                <FormControlLabel
                                    value="current"
                                    control={<Radio size="small" />}
                                    label={
                                        <Typography variant="body2">
                                            Dataset Version datetime (default
                                            {bamStatus?.version_dttm ? ` — ${bamStatus.version_dttm}` : ""})
                                        </Typography>
                                    }
                                />
                                <FormControlLabel
                                    value="custom"
                                    control={<Radio size="small" />}
                                    label={<Typography variant="body2">Custom datetime</Typography>}
                                />
                                {uploadMode === "custom" && (
                                    <TextField
                                        label="Calibration Date"
                                        type="date"
                                        size="small"
                                        value={customCalibrationDate}
                                        onChange={(e) => setCustomCalibrationDate(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        style={{ maxWidth: 220, marginLeft: 32, marginBottom: 8 }}
                                    />
                                )}
                                <FormControlLabel
                                    value="epoch"
                                    control={<Radio size="small" color="error" />}
                                    label={
                                        <Typography variant="body2" color="error">
                                            Epoch Upload (destructive)
                                        </Typography>
                                    }
                                />
                            </RadioGroup>
                            {uploadMode === "epoch" && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: "white",
                                        fontWeight: 600,
                                        backgroundColor: "error.light",
                                        borderRadius: 1,
                                        p: 1,
                                        mt: 0.5,
                                        opacity: 0.9,
                                    }}
                                >
                                    WARNING: This will DELETE all existing conductor span BAM records for every
                                    (conductor_span, rating_methodology) pair in this project, then re-upload with a
                                    fixed EPOCH datetime (1970-01-01). This is irreversible. Only use this for initial
                                    project onboarding or when a full historical reprocessing has been requested.
                                </Typography>
                            )}
                        </AccordionDetails>
                    </Accordion>

                    {/* Upload button */}
                    <div className={classes.buttonContainer} style={{ justifyContent: "flex-start" }}>
                        <ConfirmModal
                            enableButton={canUpload}
                            operation={handleBamUpload}
                            buttonText={isUploaded ? "Already Uploaded" : "Upload BAM Results"}
                            buttonColor={uploadMode === "epoch" ? "error" : hasWarnings ? "warning" : "primary"}
                            modalTitle={
                                uploadMode === "epoch" ? "CONFIRM DESTRUCTIVE EPOCH UPLOAD" : "Confirm BAM Upload"
                            }
                            modalMessage={
                                uploadMode === "epoch"
                                    ? `EPOCH UPLOAD for ${client} / ${project} (version: ${bamStatus?.version_dttm}). This will DELETE existing conductor span BAMs and re-upload with calibration_dttm = 1970-01-01.`
                                    : uploadMode === "custom"
                                      ? `Upload BAM results for ${client} / ${project} (version: ${bamStatus?.version_dttm}) with calibration_dttm = ${customCalibrationDate}?`
                                      : `Upload BAM results for ${client} / ${project} (version: ${bamStatus?.version_dttm})?`
                            }
                            warningMessage={
                                uploadMode === "epoch"
                                    ? "This action is IRREVERSIBLE. Existing BAM records will be permanently deleted."
                                    : hasWarnings
                                      ? "Validation warnings were detected — you have acknowledged these before proceeding."
                                      : undefined
                            }
                        />
                        {uploadingBam && <CircularProgress size={24} />}
                    </div>

                    {/* Map + embedded nonconformity side panel */}
                    {showMap && (
                        <div style={{ marginTop: 16 }}>
                            <div style={{ display: "flex", alignItems: "flex-start", width: "100%" }}>
                                {/* Left panel — nonconformity items */}
                                {hasNonconformity && (
                                    <div
                                        style={{
                                            width: 220,
                                            minWidth: 220,
                                            height: 750,
                                            overflowY: "auto",
                                            background: "#fafafa",
                                            borderRight: "1px solid #e0e0e0",
                                            display: "flex",
                                            flexDirection: "column",
                                            flexShrink: 0,
                                        }}
                                    >
                                        {/* Panel header + legend */}
                                        <div style={{ padding: "8px 10px 6px", borderBottom: "1px solid #eee" }}>
                                            <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                                                Nonconformity
                                            </Typography>
                                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 3 }}>
                                                {bamOutliers.length > 0 && (
                                                    <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
                                                        <span
                                                            style={{
                                                                width: 8,
                                                                height: 8,
                                                                borderRadius: "50%",
                                                                background: "#FF8C00",
                                                                display: "inline-block",
                                                                flexShrink: 0,
                                                            }}
                                                        />
                                                        <Typography variant="caption">Outliers</Typography>
                                                    </span>
                                                )}
                                                {deviceFailures.length > 0 && (
                                                    <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
                                                        <span
                                                            style={{
                                                                width: 8,
                                                                height: 8,
                                                                borderRadius: "50%",
                                                                background: "#CC0000",
                                                                display: "inline-block",
                                                                flexShrink: 0,
                                                            }}
                                                        />
                                                        <Typography variant="caption">Failures</Typography>
                                                    </span>
                                                )}
                                                {spanFailures.length > 0 && (
                                                    <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
                                                        <span
                                                            style={{
                                                                width: 8,
                                                                height: 8,
                                                                borderRadius: "50%",
                                                                background: "#FFD700",
                                                                border: "1px solid #888",
                                                                display: "inline-block",
                                                                flexShrink: 0,
                                                            }}
                                                        />
                                                        <Typography variant="caption">Spans</Typography>
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* BAM outliers */}
                                        {bamOutliers.length > 0 && (
                                            <div>
                                                <div style={{ padding: "5px 10px 3px", background: "#fff8f0" }}>
                                                    <Typography
                                                        variant="caption"
                                                        style={{ fontWeight: 700, color: "#FF8C00" }}
                                                    >
                                                        BAM Outliers ({bamOutliers.length - acknowledgedOutliers.size}/
                                                        {bamOutliers.length})
                                                    </Typography>
                                                </div>
                                                {bamOutliers.map((o) => {
                                                    const isAcked = acknowledgedOutliers.has(o.weather_site_id);
                                                    return (
                                                        <div
                                                            key={o.weather_site_id}
                                                            onClick={() => flyToCoord(o.latitude, o.longitude)}
                                                            style={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                padding: "1px 6px 1px 2px",
                                                                cursor: "pointer",
                                                                opacity: isAcked ? 0.08 : 1,
                                                                borderBottom: "1px solid #f0f0f0",
                                                            }}
                                                        >
                                                            <Checkbox
                                                                size="small"
                                                                checked={isAcked}
                                                                onClick={(e) => e.stopPropagation()}
                                                                onChange={() => toggleOutlierAck(o.weather_site_id)}
                                                                sx={{ p: "2px", mr: "2px", flexShrink: 0 }}
                                                            />
                                                            <Typography
                                                                variant="caption"
                                                                style={{
                                                                    flex: 1,
                                                                    textDecoration: isAcked ? "line-through" : "none",
                                                                    whiteSpace: "nowrap",
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                }}
                                                            >
                                                                Site {o.weather_site_id} — <strong>{o.type}</strong>{" "}
                                                                {fmt3(o.value)}
                                                            </Typography>
                                                            <Tooltip
                                                                title={`Lat: ${fmt3(o.latitude)} | Lng: ${fmt3(
                                                                    o.longitude
                                                                )} | Elev: ${fmt3(o.elevation)} m`}
                                                                placement="right"
                                                                arrow
                                                            >
                                                                <InfoOutlinedIcon
                                                                    sx={{
                                                                        fontSize: 13,
                                                                        color: "#ccc",
                                                                        flexShrink: 0,
                                                                        ml: "2px",
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {/* Device health failures */}
                                        {deviceFailures.length > 0 && (
                                            <div>
                                                <div style={{ padding: "5px 10px 3px", background: "#fff0f0" }}>
                                                    <Typography
                                                        variant="caption"
                                                        style={{ fontWeight: 700, color: "#CC0000" }}
                                                    >
                                                        Device Health Failures ({deviceFailures.length})
                                                    </Typography>
                                                </div>
                                                {deviceFailures.map((f) => (
                                                    <div
                                                        key={f.weather_site_id}
                                                        onClick={() => flyToCoord(f.latitude, f.longitude)}
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            padding: "4px 6px",
                                                            cursor: "pointer",
                                                            borderBottom: "1px solid #f0f0f0",
                                                        }}
                                                    >
                                                        <Typography variant="caption" style={{ flex: 1 }}>
                                                            Site {f.weather_site_id}
                                                        </Typography>
                                                        <Tooltip
                                                            title={`Lat: ${fmt3(f.latitude)} | Lng: ${fmt3(
                                                                f.longitude
                                                            )} | Elev: ${fmt3(f.elevation)} m`}
                                                            placement="right"
                                                            arrow
                                                        >
                                                            <InfoOutlinedIcon
                                                                sx={{ fontSize: 13, color: "#ccc", flexShrink: 0 }}
                                                            />
                                                        </Tooltip>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Span validation failures */}
                                        {spanFailures.length > 0 && (
                                            <div>
                                                <div
                                                    style={{
                                                        padding: "5px 10px 3px",
                                                        background: "#fffde0",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "space-between",
                                                    }}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        style={{ fontWeight: 700, color: "#9b8000" }}
                                                    >
                                                        Span Failures (
                                                        {spanFailures.length - acknowledgedSpanFailures.size}/
                                                        {spanFailures.length})
                                                    </Typography>
                                                    {spanFailures.some(
                                                        (s) => Math.abs(s.change_percentage) >= 0.15
                                                    ) && (
                                                        <Tooltip
                                                            title="Acknowledge all spans with >15% change"
                                                            placement="left"
                                                            arrow
                                                        >
                                                            <FormControlLabel
                                                                control={
                                                                    <Checkbox
                                                                        size="small"
                                                                        checked={spanFailures
                                                                            .filter(
                                                                                (s) =>
                                                                                    Math.abs(s.change_percentage) >=
                                                                                    0.15
                                                                            )
                                                                            .every((s) =>
                                                                                acknowledgedSpanFailures.has(s.span_id)
                                                                            )}
                                                                        indeterminate={
                                                                            spanFailures
                                                                                .filter(
                                                                                    (s) =>
                                                                                        Math.abs(s.change_percentage) >=
                                                                                        0.15
                                                                                )
                                                                                .some((s) =>
                                                                                    acknowledgedSpanFailures.has(
                                                                                        s.span_id
                                                                                    )
                                                                                ) &&
                                                                            !spanFailures
                                                                                .filter(
                                                                                    (s) =>
                                                                                        Math.abs(s.change_percentage) >=
                                                                                        0.15
                                                                                )
                                                                                .every((s) =>
                                                                                    acknowledgedSpanFailures.has(
                                                                                        s.span_id
                                                                                    )
                                                                                )
                                                                        }
                                                                        onChange={toggleAllSignificantSpanFailuresAck}
                                                                        sx={{ p: "2px" }}
                                                                    />
                                                                }
                                                                label={
                                                                    <Typography
                                                                        variant="caption"
                                                                        style={{ color: "#9b8000", fontSize: 10 }}
                                                                    >
                                                                        Ack all &gt;15%
                                                                    </Typography>
                                                                }
                                                                sx={{ m: 0 }}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                </div>
                                                {spanFailures.map((s) => {
                                                    const isAcked = acknowledgedSpanFailures.has(s.span_id);
                                                    return (
                                                        <div
                                                            key={s.span_id}
                                                            onClick={() => flyToCoord(s.mid_latitude, s.mid_longitude)}
                                                            style={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                padding: "1px 6px 1px 2px",
                                                                cursor: "pointer",
                                                                opacity: isAcked ? 0.08 : 1,
                                                                borderBottom: "1px solid #f0f0f0",
                                                                borderLeft: "3px solid #d32f2f",
                                                            }}
                                                        >
                                                            <Checkbox
                                                                size="small"
                                                                checked={isAcked}
                                                                onClick={(e) => e.stopPropagation()}
                                                                onChange={() => toggleSpanFailureAck(s.span_id)}
                                                                sx={{ p: "2px", mr: "2px", flexShrink: 0 }}
                                                            />
                                                            <Typography
                                                                variant="caption"
                                                                style={{
                                                                    flex: 1,
                                                                    textDecoration: isAcked ? "line-through" : "none",
                                                                    whiteSpace: "nowrap",
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                }}
                                                            >
                                                                {s.span_id} —{" "}
                                                                <strong>{fmt3(s.change_percentage * 100)}%</strong>
                                                            </Typography>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Map (takes remaining width) + floating layer toggles */}
                                <div style={{ flex: 1, minWidth: 0, position: "relative" }}>
                                    <MapleMap
                                        simulationAreas={simAreaDataset ?? { simulation_areas: [] }}
                                        simulationSpans={simSpanDataset ?? { spans: [] }}
                                        allSpanCoefficients={
                                            layerVisibility.coefficients
                                                ? palmCoeffDataset?.span_coefficients
                                                : undefined
                                        }
                                        mapleSpanBams={layerVisibility.spanBams ? spanBamsDataset?.results : undefined}
                                        mapleWeatherSiteBams={
                                            layerVisibility.weatherSiteBams
                                                ? weatherSiteBamsDataset?.results
                                                : undefined
                                        }
                                        mapleSpanBamDiffs={layerVisibility.bamDiff ? spanBamDiffs : undefined}
                                        center={mapCenter}
                                        zoom={mapZoom}
                                        satelliteView={satelliteView}
                                        mapleNonconformity={
                                            visibleNonconformity
                                                ? {
                                                      ...visibleNonconformity,
                                                      bam_outliers: layerVisibility.bamOutliers
                                                          ? visibleNonconformity.bam_outliers
                                                          : [],
                                                      device_health_failures: layerVisibility.deviceFailures
                                                          ? visibleNonconformity.device_health_failures
                                                          : [],
                                                      span_validation_failures: layerVisibility.spanFailures
                                                          ? visibleNonconformity.span_validation_failures
                                                          : [],
                                                  }
                                                : null
                                        }
                                    />
                                    {/* Floating layer toggle panel */}
                                    <div
                                        style={{
                                            position: "absolute",
                                            top: 10,
                                            right: 10,
                                            background: "rgba(255,255,255,0.93)",
                                            borderRadius: 8,
                                            padding: "8px 10px",
                                            boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                                            zIndex: 2,
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 2,
                                            minWidth: 160,
                                        }}
                                    >
                                        <Typography variant="caption" style={{ fontWeight: 700, marginBottom: 2 }}>
                                            Layers
                                        </Typography>
                                        {palmCoeffDataset && (
                                            <label
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 4,
                                                    cursor: "pointer",
                                                }}
                                            >
                                                <Checkbox
                                                    size="small"
                                                    checked={layerVisibility.coefficients}
                                                    onChange={() => toggleLayer("coefficients")}
                                                    sx={{ p: 0 }}
                                                />
                                                <Typography variant="caption">Coefficients</Typography>
                                            </label>
                                        )}
                                        {spanBamsDataset && (
                                            <label
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 4,
                                                    cursor: "pointer",
                                                }}
                                            >
                                                <Checkbox
                                                    size="small"
                                                    checked={layerVisibility.spanBams}
                                                    onChange={() => toggleLayer("spanBams")}
                                                    sx={{ p: 0 }}
                                                />
                                                <span
                                                    style={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: "50%",
                                                        background: "#1976d2",
                                                        display: "inline-block",
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <Typography variant="caption">Span BAMs</Typography>
                                            </label>
                                        )}
                                        {weatherSiteBamsDataset && (
                                            <label
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 4,
                                                    cursor: "pointer",
                                                }}
                                            >
                                                <Checkbox
                                                    size="small"
                                                    checked={layerVisibility.weatherSiteBams}
                                                    onChange={() => toggleLayer("weatherSiteBams")}
                                                    sx={{ p: 0 }}
                                                />
                                                <span
                                                    style={{
                                                        width: 10,
                                                        height: 10,
                                                        background: "#1976d2",
                                                        display: "inline-block",
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <Typography variant="caption">Weather Site BAMs</Typography>
                                            </label>
                                        )}
                                        {spanBamDiffs && (
                                            <label
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 4,
                                                    cursor: "pointer",
                                                }}
                                            >
                                                <Checkbox
                                                    size="small"
                                                    checked={layerVisibility.bamDiff}
                                                    onChange={() => toggleLayer("bamDiff")}
                                                    sx={{ p: 0 }}
                                                />
                                                <span
                                                    style={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: "50%",
                                                        background: "linear-gradient(135deg, #d32f2f 50%, #43a047 50%)",
                                                        display: "inline-block",
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <Typography variant="caption">BAM Diff</Typography>
                                            </label>
                                        )}
                                        {bamOutliers.length > 0 && (
                                            <label
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 4,
                                                    cursor: "pointer",
                                                }}
                                            >
                                                <Checkbox
                                                    size="small"
                                                    checked={layerVisibility.bamOutliers}
                                                    onChange={() => toggleLayer("bamOutliers")}
                                                    sx={{ p: 0 }}
                                                />
                                                <span
                                                    style={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: "50%",
                                                        background: "#FF8C00",
                                                        display: "inline-block",
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <Typography variant="caption">
                                                    BAM Outliers ({bamOutliers.length})
                                                </Typography>
                                            </label>
                                        )}
                                        {deviceFailures.length > 0 && (
                                            <label
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 4,
                                                    cursor: "pointer",
                                                }}
                                            >
                                                <Checkbox
                                                    size="small"
                                                    checked={layerVisibility.deviceFailures}
                                                    onChange={() => toggleLayer("deviceFailures")}
                                                    sx={{ p: 0 }}
                                                />
                                                <span
                                                    style={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: "50%",
                                                        background: "#CC0000",
                                                        display: "inline-block",
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <Typography variant="caption">
                                                    Device Health Failures ({deviceFailures.length})
                                                </Typography>
                                            </label>
                                        )}
                                        {spanFailures.length > 0 && (
                                            <label
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 4,
                                                    cursor: "pointer",
                                                }}
                                            >
                                                <Checkbox
                                                    size="small"
                                                    checked={layerVisibility.spanFailures}
                                                    onChange={() => toggleLayer("spanFailures")}
                                                    sx={{ p: 0 }}
                                                />
                                                <span
                                                    style={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: "50%",
                                                        background: "#FFD700",
                                                        border: "1px solid #888",
                                                        display: "inline-block",
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <Typography variant="caption">
                                                    Span Failures ({spanFailures.length})
                                                </Typography>
                                            </label>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div style={{ marginTop: 8, display: "flex", justifyContent: "center" }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    onClick={() => setSatelliteView((prev) => !prev)}
                                >
                                    {satelliteView ? "Switch to Street View" : "Switch to Satellite View"}
                                </Button>
                            </div>
                        </div>
                    )}
                </Paper>
            )}

            {/* ── Tab 2: Section Rating Methodology ──────────────────────── */}
            {selectedTab === 2 && (
                <Paper variant="outlined" className={classes.sectionCard}>
                    <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "16px", padding: "8px 0", marginBottom: "8px", borderBottom: "1px solid #e0e0e0" }}>
                            <Typography variant="h6" style={{ marginRight: "auto" }}>
                                <strong>Change Section Methodology</strong>
                            </Typography>
                            <DropdownWithAutocomplete
                                label="Rating Methodology"
                                setValue={setSectionRatingMethodology}
                                options={RATING_METHODOLOGY_OPTIONS}
                                value={sectionRatingMethodology}
                                refreshDisabled={true}
                                refreshOptions={() => {}}
                                disabled={false}
                                isLoading={false}
                                styleClass={{ minWidth: "250px" }}
                            />
                            <ConfirmModal
                                enableButton={!!client && !!project && sectionRatingMethodology !== ""}
                                operation={changeRatingMethodology}
                                buttonText="Submit"
                                modalTitle="Confirm methodology change"
                                modalMessage={`Are you sure you want to change the section rating methodology for ${client}/${project} to ${sectionRatingMethodology}`}
                                modalNewLineMessage={undefined}
                                warningMessage={undefined}
                            />
                        </div>
                        <Accordion disableGutters>
                            <AccordionSummary>
                                <Typography variant="h6">
                                    <strong>Current Section Methodology Summary</strong>
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails style={{ display: "block" }}>
                                {sectionMethodologySummary && (
                                    <h3>Total Conductor Spans: {sectionMethodologySummary.conductor_span_count}</h3>
                                )}
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell colSpan={3} align="left"><strong>BAM Coverage by Methodology</strong></TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell><strong>Methodology</strong></TableCell>
                                            <TableCell><strong>Span Count</strong></TableCell>
                                            <TableCell><strong>Most Recent Calibration</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {sectionMethodologySummary?.bam_coverage_by_methodology.map((row, i) => (
                                            <TableRow key={i}>
                                                <TableCell>{row.methodology}</TableCell>
                                                <TableCell>{row.conductor_span_bam_count}</TableCell>
                                                <TableCell>{row.most_recent_calibration_dttm}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                                <Table size="small" style={{ marginTop: "16px" }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell colSpan={3} align="left"><strong>Section Summary</strong></TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell><strong>Section ID</strong></TableCell>
                                            <TableCell><strong>Section Name</strong></TableCell>
                                            <TableCell><strong>Current Methodology</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {sectionMethodologySummary?.sections.map((section) => (
                                            <TableRow key={section.section_id}>
                                                <TableCell>{section.section_id}</TableCell>
                                                <TableCell>{section.section_name}</TableCell>
                                                <TableCell>{section.current_methodology}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </AccordionDetails>
                        </Accordion>
                    </div>
                </Paper>
            )}
        </div>
    );
}
